import { Box, Chip, Stack, Typography } from '@mui/material';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PASEK DOBY OPERACYJNEJ — element podpisowy produktu
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Żaden panel administracyjny tego nie ma, a w TuSzamie to najważniejsza oś odniesienia: biznes
 * jest rządzony DOBOWYM ZEGAREM. Zamówienia da się złożyć tylko do cutoffu, potem rusza produkcja,
 * potem dostawa, a po dostawie liczby na dziś już się nie zmienią.
 *
 * Dlaczego to jest element powłoki, a nie kafelek na jednym ekranie: ZNACZENIE każdej liczby
 * zależy od położenia na tej osi. Rano „30 tys. zł" znaczy „tyle już zamówiono", po dostawach
 * znaczy „tyle było". Ta sama liczba, dwa różne zdania — i użytkownik musi wiedzieć które,
 * niezależnie od tego, na który ekran właśnie patrzy.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ CO TU JEST DANYMI, A CO NIE                                                              │
 * │ Godziny faz to STAŁE DZIEDZINOWE opisujące typowy dzień, nie odczyt z bazy. Rzeczywisty  │
 * │ cutoff jest ustalany PER PARA szkoła–restauracja (OrderFinishTime) i bywa różny.         │
 * │ Dlatego pasek mówi „typowy dzień" i nie podaje żadnej liczby, która wyglądałaby na       │
 * │ zmierzoną. Gdy Control Center dostanie odczyt realnych cutoffów, pasek pokaże rozrzut —  │
 * │ do tego czasu NIE UDAJE, że go zna.                                                      │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 */

type Faza = { godzina: number; etykieta: string };

const FAZY: readonly Faza[] = [
  { godzina: 6, etykieta: 'Otwarcie zamówień' },
  { godzina: 9.5, etykieta: 'Cutoff' },
  { godzina: 10.5, etykieta: 'Produkcja' },
  { godzina: 12, etykieta: 'Dostawy' },
  { godzina: 14.5, etykieta: 'Po dostawie' },
  { godzina: 17, etykieta: 'Zamknięcie dnia' },
];

const START = 6;
const KONIEC = 17;

function terazWGodzinach(zegar: Date): number {
  return zegar.getHours() + zegar.getMinutes() / 60;
}

/** Faza, w której właśnie jesteśmy — czyli ostatnia, której godzina już minęła. */
export function biezacaFaza(godzina: number): Faza | null {
  const minione = FAZY.filter((f) => f.godzina <= godzina);
  return minione.length ? minione[minione.length - 1]! : null;
}

/**
 * Zdanie opisujące, co pozycja w dobie znaczy dla liczb na ekranie.
 * Eksportowane, bo dokładnie ta sama informacja jest potrzebna w dowodzie przy metryce dziennej —
 * a dwa niezależnie napisane opisy tej samej rzeczy rozjeżdżają się przy pierwszej zmianie.
 */
export function znaczenieFazy(godzina: number): string {
  if (godzina < 9.5) return 'Przed cutoffem — liczba zamówień na dziś jeszcze rośnie';
  if (godzina < 12) return 'Po cutoffie — liczba zamówień na dziś już nie rośnie';
  if (godzina < 14.5) return 'W trakcie dostaw — punktualność jeszcze się zmienia';
  if (godzina < 17) return 'Po dostawach — liczby na dziś są zamknięte';
  return 'Dzień zamknięty';
}

export function PasekDoby({ zegar = new Date() }: { zegar?: Date }) {
  const godzina = terazWGodzinach(zegar);
  const postep = Math.max(0, Math.min(100, ((godzina - START) / (KONIEC - START)) * 100));
  const teraz = biezacaFaza(godzina);
  const czas = `${String(zegar.getHours()).padStart(2, '0')}:${String(zegar.getMinutes()).padStart(2, '0')}`;

  return (
    <Box
      sx={{
        flex: 'none',
        px: { xs: 2, md: 4 },
        pt: 1.5,
        pb: 2,
        borderBottom: '1px solid var(--linia)',
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--tlo) 92%, transparent), transparent)',
        display: { xs: 'none', sm: 'block' },
        /*
         * Ostatni znacznik doby stoi na 100% toru i jest wyśrodkowany na swojej godzinie, więc
         * jego POJEMNIK wystaje kilka pikseli poza kadr — a to wystarczy, żeby cała strona
         * dostała poziomy pasek przewijania. Etykieta ma już własne przesunięcie do środka,
         * więc nic czytelnego nie ginie.
         *
         * `clip`, nie `hidden`: `hidden` tworzy kontener przewijania i potrafi psuć przyklejone
         * pozycjonowanie sąsiadów. `clip` przycina bez zmiany zachowania układu.
         */
        overflowX: 'clip',
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', mb: 1.25, flexWrap: 'wrap' }}>
        <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
          Doba operacyjna · typowy dzień
        </Typography>
        <Typography sx={{ fontFamily: 'var(--krój-display)', fontSize: 15 }} className="sygnal-liczba">
          {czas}
        </Typography>
        <Chip
          size="small"
          label={znaczenieFazy(godzina)}
          sx={{
            background: 'rgba(247, 177, 11, 0.16)',
            color: 'var(--zloto-ciemne)',
            fontSize: 11.5,
            fontWeight: 600,
          }}
        />
      </Stack>

      <Box sx={{ position: 'relative', height: 40 }} role="img" aria-label={`Doba operacyjna, ${czas}. ${znaczenieFazy(godzina)}.`}>
        <Box
          sx={{
            position: 'absolute',
            top: 13,
            left: 0,
            right: 0,
            height: 4,
            borderRadius: '2px',
            background: 'var(--tlo-glebokie)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${postep.toFixed(1)}%`,
              borderRadius: '2px',
              background: 'linear-gradient(90deg, var(--zloto-glebokie), var(--zloto))',
            }}
          />
        </Box>

        {FAZY.map((f, i) => {
          const lewo = ((f.godzina - START) / (KONIEC - START)) * 100;
          const minela = f.godzina <= godzina;
          const jestTeraz = teraz?.etykieta === f.etykieta;
          return (
            <Stack
              key={f.etykieta}
              sx={{
                position: 'absolute',
                top: 0,
                left: `${lewo.toFixed(1)}%`,
                transform: 'translateX(-50%)',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  mt: '9px',
                  background: minela ? 'var(--zloto)' : 'var(--powierzchnia)',
                  border: `3px solid ${minela ? 'var(--zloto)' : 'var(--linia-mocna)'}`,
                  ...(jestTeraz
                    ? {
                        background: 'var(--powierzchnia)',
                        borderColor: 'var(--zloto)',
                        transform: 'scale(1.34)',
                        boxShadow: '0 0 0 6px rgba(247, 177, 11, 0.18)',
                      }
                    : {}),
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: minela ? 'var(--tekst)' : 'var(--tekst-slaby)',
                  // Skrajne etykiety muszą zawinąć się do środka, inaczej połowa podpisu wychodzi
                  // poza płótno i tekst jest ucięty. Przesuwamy PODPIS, nie znacznik — kropka ma
                  // zostać dokładnie na swojej godzinie.
                  transform:
                    i === 0
                      ? 'translateX(calc(50% - 6px))'
                      : i === FAZY.length - 1
                        ? 'translateX(calc(-50% + 6px))'
                        : undefined,
                }}
              >
                {f.etykieta}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}
